import { apiClient } from './api'

export type ClusterStatus = {
  id: string
  name: string
  location: string
  provider_org: string
  status: 'healthy' | 'warning' | 'critical' | 'offline'
  version: string
  uptime: string
  nodes: number
  vms: number
  /**
   * Uso MEDIDO. `null` quando a plataforma não conseguiu ler o cluster — não
   * existe "0% de um cluster que não respondeu" (issue #895). Quem exibe tem de
   * tratar a ausência; a capacidade cadastrada está em `capacity`.
   */
  resources: {
    cpu: ResourceMetric
    ram: ResourceMetric
    storage: ResourceMetric
  } | null
  /** A leitura chegou, mas faltou algum node — os números estão subcontados. */
  resources_partial?: boolean
  /** Nodes que não responderam na coleta. */
  resources_failed_nodes?: string[]
  /** Capacidade CADASTRADA (snapshot do banco). Não é medição. */
  capacity?: {
    cpu: number
    ram: number
    storage: number
  }
  alerts: Alert[]
  last_seen: string
}

export type ResourceMetric = {
  total: number
  used: number
  usage_percent: number
}

export type Alert = {
  level: 'info' | 'warning' | 'critical'
  message: string
  time: string
}

export type NetworkTopology = {
  nodes: NetworkNode[]
  edges: NetworkEdge[]
}

export type NetworkNode = {
  id: string
  type: 'internet' | 'firewall' | 'datacenter' | 'cluster'
  label: string
  status?: 'healthy' | 'warning' | 'critical'
  x: number
  y: number
}

export type NetworkEdge = {
  from: string
  to: string
}

export type ServiceStatus = {
  name: string
  type: 'database' | 'cache' | 'api' | 'worker' | 'proxy'
  status: 'healthy' | 'warning' | 'critical'
  response_time: number | null
  version: string
  description: string
  metrics: Record<string, any>
}

export type ResourceUtilization = {
  period: {
    start: string
    end: string
    hours: number
  }
  data: UtilizationDataPoint[]
  summary: {
    avg_cpu: number
    avg_ram: number
    avg_storage: number
    peak_cpu: number
    peak_ram: number
  }
}

export type UtilizationDataPoint = {
  timestamp: string
  cpu_usage: number
  ram_usage: number
  storage_usage: number
  network_in: number
  network_out: number
}

export type InfrastructureAlert = {
  id: string
  severity: 'info' | 'warning' | 'critical'
  title: string
  description: string
  source: string
  timestamp: string
  status: 'active' | 'resolved'
  acknowledged: boolean
  tags: string[]
}

export type CapacityPlanning = {
  current_utilization: {
    cpu: { used: number; total: number; percentage: number }
    ram: { used: number; total: number; percentage: number }
    storage: { used: number; total: number; percentage: number }
  }
  growth_trend: {
    cpu_monthly: number
    ram_monthly: number
    storage_monthly: number
  }
  predictions: {
    cpu_full_in_months: number
    ram_full_in_months: number
    storage_full_in_months: number
  }
  recommendations: CapacityRecommendation[]
}

export type CapacityRecommendation = {
  type: 'expansion' | 'optimization'
  priority: 'low' | 'medium' | 'high'
  resource: 'cpu' | 'ram' | 'storage'
  description: string
  estimated_cost?: number
  estimated_savings?: number
}

export const infrastructureApi = {
  // Get clusters status
  async getClustersStatus(): Promise<ClusterStatus[]> {
    return apiClient.get<ClusterStatus[]>('/api/v1/infrastructure/clusters')
  },

  // Get network topology
  async getNetworkTopology(): Promise<NetworkTopology> {
    return apiClient.get<NetworkTopology>('/api/v1/infrastructure/network-topology')
  },

  // Get services status
  async getServicesStatus(): Promise<ServiceStatus[]> {
    return apiClient.get<ServiceStatus[]>('/api/v1/infrastructure/services-status')
  },

  // Get resource utilization
  async getResourceUtilization(periodHours: number = 24): Promise<ResourceUtilization> {
    return apiClient.get<ResourceUtilization>(
      `/api/v1/infrastructure/resource-utilization?period_hours=${periodHours}`,
    )
  },

  // Get infrastructure alerts
  async getAlerts(
    limit: number = 50,
    severity?: 'info' | 'warning' | 'critical',
  ): Promise<InfrastructureAlert[]> {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (severity) params.append('severity', severity)

    return apiClient.get<InfrastructureAlert[]>(
      `/api/v1/infrastructure/alerts?${params.toString()}`,
    )
  },

  // Acknowledge alert
  async acknowledgeAlert(alertId: string): Promise<any> {
    return apiClient.post<any>(`/api/v1/infrastructure/alerts/${alertId}/acknowledge`)
  },

  // Get capacity planning
  async getCapacityPlanning(): Promise<CapacityPlanning> {
    return apiClient.get<CapacityPlanning>('/api/v1/infrastructure/capacity-planning')
  },
}
